import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { User, Camera, Mail, Phone, Shield, ShieldAlert, Download, Trash, Save } from "lucide-react";

interface UserProfile {
  id: string;
  name: string;
  email: string;
  photoUrl?: string;
  phone?: string;
  consents: {
    marketing: boolean;
    cookies: boolean;
    dataProcessing: boolean;
    thirdParties: boolean;
  };
}

const ProfileEditModule = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  
  // Profile state
  const [profileData, setProfileData] = useState<UserProfile>({
    id: profile?.id || "1",
    name: profile?.full_name || "Usuário Teste",
    email: profile?.email || "teste@example.com",
    photoUrl: profile?.avatar_url,
    phone: "",
    consents: {
      marketing: true,
      cookies: true,
      dataProcessing: true,
      thirdParties: false
    }
  });
  
  // Form state
  const [formData, setFormData] = useState<UserProfile>(profileData);
  const [password, setPassword] = useState({
    current: "",
    new: "",
    confirm: ""
  });
  
  // Update form data when profile changes
  useEffect(() => {
    if (profile) {
      const updatedProfile = {
        ...profileData,
        id: profile.id,
        name: profile.full_name || "",
        email: profile.email,
        photoUrl: profile.avatar_url
      };
      setProfileData(updatedProfile);
      setFormData(updatedProfile);
    }
  }, [profile]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const handleConsentChange = (consentType: keyof UserProfile['consents'], checked: boolean) => {
    setFormData({
      ...formData,
      consents: {
        ...formData.consents,
        [consentType]: checked
      }
    });
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPassword({ ...password, [name]: value });
  };
  
  const handleProfileUpdate = () => {
    // In a real implementation, you would call an API endpoint
    setProfileData(formData);
    
    toast({
      title: "Perfil atualizado",
      description: "Suas informações foram salvas com sucesso.",
    });
  };
  
  const handlePasswordUpdate = () => {
    // Validate passwords
    if (password.new !== password.confirm) {
      toast({
        title: "Erro",
        description: "As novas senhas não coincidem.",
        variant: "destructive",
      });
      return;
    }
    
    // In a real implementation, you would call an API endpoint
    setPassword({
      current: "",
      new: "",
      confirm: ""
    });
    
    toast({
      title: "Senha atualizada",
      description: "Sua senha foi alterada com sucesso.",
    });
  };
  
  const handleDataDownload = () => {
    // In a real implementation, this would generate and download a file
    toast({
      title: "Download iniciado",
      description: "Seus dados estão sendo preparados para download.",
    });
  };

  const handleDeleteAccount = () => {
    // In a real implementation, this would call an API to delete the account
    toast({
      title: "Conta excluída",
      description: "Sua conta foi programada para exclusão.",
      variant: "destructive",
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Informações Pessoais</CardTitle>
            <CardDescription>
              Atualize suas informações de perfil
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="relative">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={formData.photoUrl} alt={formData.name} />
                  <AvatarFallback className="text-xl">
                    {formData.name.split(" ").map(n => n[0]).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute bottom-0 right-0">
                  <Button size="icon" className="h-8 w-8 rounded-full" variant="secondary">
                    <Camera className="h-4 w-4" />
                    <span className="sr-only">Alterar foto</span>
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 flex-1">
                <div className="grid w-full items-center gap-1.5">
                  <Label htmlFor="name">Nome completo</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="flex">
                  <Input
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="rounded-r-none"
                  />
                  <div className="bg-muted flex items-center px-3 rounded-r-md">
                    <Badge variant="outline" className="whitespace-nowrap">
                      Verificado
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-6 flex justify-end">
            <Button onClick={handleProfileUpdate}>
              <Save className="mr-2 h-4 w-4" />
              Salvar Alterações
            </Button>
          </CardFooter>
        </Card>
        
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Segurança</CardTitle>
            <CardDescription>
              Gerencie sua senha e configurações de segurança
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current">Senha atual</Label>
              <Input
                id="current"
                name="current"
                type="password"
                value={password.current}
                onChange={handlePasswordChange}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new">Nova senha</Label>
              <Input
                id="new"
                name="new"
                type="password"
                value={password.new}
                onChange={handlePasswordChange}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirmar senha</Label>
              <Input
                id="confirm"
                name="confirm"
                type="password"
                value={password.confirm}
                onChange={handlePasswordChange}
              />
            </div>
          </CardContent>
          <CardFooter className="border-t pt-6">
            <Button 
              onClick={handlePasswordUpdate} 
              className="w-full"
              disabled={!password.current || !password.new || !password.confirm}
            >
              <Shield className="mr-2 h-4 w-4" />
              Atualizar Senha
            </Button>
          </CardFooter>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Privacidade e LGPD</CardTitle>
          <CardDescription>
            Gerencie como seus dados são processados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h4 className="font-medium">Emails de marketing</h4>
                <p className="text-sm text-muted-foreground">
                  Receber emails sobre novidades e dicas de saúde mental
                </p>
              </div>
              <Switch
                checked={formData.consents.marketing}
                onCheckedChange={(checked) => handleConsentChange("marketing", checked)}
              />
            </div>
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h4 className="font-medium">Cookies essenciais</h4>
                <p className="text-sm text-muted-foreground">
                  Necessários para o funcionamento do site
                </p>
              </div>
              <Switch checked disabled />
            </div>
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h4 className="font-medium">Cookies de análise</h4>
                <p className="text-sm text-muted-foreground">
                  Ajudam a melhorar nossos serviços
                </p>
              </div>
              <Switch
                checked={formData.consents.cookies}
                onCheckedChange={(checked) => handleConsentChange("cookies", checked)}
              />
            </div>
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h4 className="font-medium">Processamento de dados</h4>
                <p className="text-sm text-muted-foreground">
                  Permitir processamento para melhorar atendimentos
                </p>
              </div>
              <Switch
                checked={formData.consents.dataProcessing}
                onCheckedChange={(checked) => handleConsentChange("dataProcessing", checked)}
              />
            </div>
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h4 className="font-medium">Compartilhamento com terceiros</h4>
                <p className="text-sm text-muted-foreground">
                  Compartilhar dados com parceiros
                </p>
              </div>
              <Switch
                checked={formData.consents.thirdParties}
                onCheckedChange={(checked) => handleConsentChange("thirdParties", checked)}
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="border-t pt-6 flex justify-between">
          <Button variant="outline" onClick={handleDataDownload}>
            <Download className="mr-2 h-4 w-4" />
            Baixar Meus Dados
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash className="mr-2 h-4 w-4" />
                Excluir Conta
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação não pode ser desfeita. Isso excluirá permanentemente sua conta
                  e removerá seus dados dos nossos servidores.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteAccount}>
                  Sim, excluir minha conta
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ProfileEditModule;
